/* Medical Equipment landing page (sections/mt-me-*.liquid).
   Part 1 is the original page's animations.js runtime (reveal, bento
   spotlight), unchanged except that the accent colour is read from the page
   wrapper. Part 2 re-implements the original React page behaviour in plain
   JS. */
/* Every mt-me-* section includes this file; run it once per page. */
if (!window.__meLpLoaded) {
window.__meLpLoaded = true;
(function () {
  "use strict";

  // ──────────────────────────────────────────────────────────────
  // Utilities
  // ──────────────────────────────────────────────────────────────
  const ACCENT_RGB = () => {
    // read --accent so animations recolor when Tweaks change the accent
    const hex = getComputedStyle(document.querySelector(".me-lp") || document.documentElement)
      .getPropertyValue("--accent").trim().replace("#", "");
    const x = hex.length === 3 ? hex.replace(/./g, c => c + c) : hex;
    if (!x) return [167, 139, 250]; // default violet
    return [parseInt(x.slice(0, 2), 16), parseInt(x.slice(2, 4), 16), parseInt(x.slice(4, 6), 16)];
  };

  // ──────────────────────────────────────────────────────────────
  // 1. Typewriter (hero h1)
  // ──────────────────────────────────────────────────────────────
  // Looks for [data-typewriter] elements with a data-text attribute,
  // types it character-by-character, then fires "tw:done" so the
  // bold tagline beneath can reveal.
  window.MT_initTypewriter = function (root = document) {
    root.querySelectorAll("[data-typewriter]:not([data-tw-on])").forEach((el) => {
      el.dataset.twOn = "1";
      const text = el.dataset.text || el.textContent;
      const speed = parseInt(el.dataset.speed || "38", 10);
      const initial = parseInt(el.dataset.initialDelay || "300", 10);
      el.innerHTML = "";
      const span = document.createElement("span");
      span.className = "mt-tw-content";
      const cursor = document.createElement("span");
      cursor.className = "mt-tw-cursor";
      cursor.textContent = "|";
      el.append(span, cursor);

      let i = 0;
      let target = span; // chars normally land in `span`; "{…}" redirects into an emphasis span
      const tick = () => {
        if (i < text.length) {
          const ch = text[i++];
          // "|" in data-text means: insert a hard line break here.
          // "{ … }" wraps the enclosed run in an emphasis span (extra sheen).
          if (ch === "|") {
            span.appendChild(document.createElement("br"));
          } else if (ch === "{") {
            const em = document.createElement("span");
            em.className = "mt-tw-em";
            span.appendChild(em);
            target = em;
          } else if (ch === "}") {
            target = span;
          } else {
            target.appendChild(document.createTextNode(ch));
          }
          setTimeout(tick, speed);
        } else {
          setTimeout(() => cursor.classList.add("is-done"), 900);
          el.dispatchEvent(new CustomEvent("tw:done", { bubbles: true }));
        }
      };
      // Start typing once the heading scrolls into view (so below-fold
      // typewriters animate when the user reaches them, not on load).
      const start = () => setTimeout(tick, initial);
      if ("IntersectionObserver" in window) {
        const obs = new IntersectionObserver((entries) => {
          if (entries[0].isIntersecting) { obs.disconnect(); start(); }
        }, { threshold: 0.4 });
        obs.observe(el);
      } else {
        start();
      }
    });
  };

  // ──────────────────────────────────────────────────────────────
  // Word-by-word reveal: splits [data-word-reveal] text into words
  // that fade + rise in sequence once scrolled into view.
  // ──────────────────────────────────────────────────────────────
  window.MT_initWordReveal = function (root = document) {
    root.querySelectorAll("[data-word-reveal]:not([data-wr-on])").forEach((el) => {
      el.dataset.wrOn = "1";
      const words = (el.textContent || "").trim().split(/\s+/);
      const wDelay = parseInt(el.dataset.wrDelay || "45", 10);
      el.textContent = "";
      words.forEach((w, i) => {
        const span = document.createElement("span");
        span.className = "mt-wr-word";
        span.textContent = w;
        span.style.transitionDelay = `${i * wDelay}ms`;
        el.appendChild(span);
        el.appendChild(document.createTextNode(" "));
      });
      const reveal = () => el.classList.add("is-in");
      if ("IntersectionObserver" in window) {
        const obs = new IntersectionObserver((entries) => {
          if (entries[0].isIntersecting) { obs.disconnect(); reveal(); }
        }, { threshold: 0.3 });
        obs.observe(el);
      } else { reveal(); }
    });
  };

  // ──────────────────────────────────────────────────────────────
  // 2. Reveal on scroll (.mt-reveal)
  // ──────────────────────────────────────────────────────────────
  window.MT_initReveal = function (root = document) {
    if (!("IntersectionObserver" in window)) {
      root.querySelectorAll(".mt-reveal").forEach(el => el.classList.add("is-in"));
      return;
    }
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          e.target.classList.add("is-in");
          obs.unobserve(e.target);
        }
      });
    }, { threshold: 0.08, rootMargin: "0px 0px -36px 0px" });
    root.querySelectorAll(".mt-reveal:not(.is-in)").forEach((el) => obs.observe(el));
  };

  // ──────────────────────────────────────────────────────────────
  // 3. Count-up numbers ([data-countup])
  // ──────────────────────────────────────────────────────────────
  // Continuous bento-style drifting particles while hovering a card,
  // same look as the Pass-or-Don't-Pay "Instant Impact" cards (.mt-bento-particle).
  window.MT_revParticles = function (card, action) {
    if (!card) return;
    if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    if (action === "start") {
      if (card._revOn) return;
      card._revOn = true;
      const [r, g, b] = ACCENT_RGB();
      const spawn = () => {
        if (!card._revOn) return;
        for (let i = 0; i < 5; i++) {
          const p = document.createElement("div");
          p.className = "mt-bento-particle";
          p.style.background = `rgba(${r},${g},${b},1)`;
          p.style.boxShadow = `0 0 5px rgba(${r},${g},${b},.6)`;
          p.style.left = Math.random() * card.clientWidth + "px";
          p.style.top = Math.random() * card.clientHeight + "px";
          card.appendChild(p);
          const dx = (Math.random() - 0.5) * 30;
          const dy = (Math.random() - 0.5) * 30;
          p.animate(
            [{ transform: "translate(0,0)", opacity: 1 },
             { transform: `translate(${dx}px,${dy}px)`, opacity: 0 }],
            { duration: 1800 + Math.random() * 800, easing: "ease-out", fill: "forwards" }
          );
          setTimeout(() => p.remove(), 2600);
        }
        card._revTimer = setTimeout(spawn, 650);
      };
      spawn();
    } else {
      card._revOn = false;
      clearTimeout(card._revTimer);
    }
  };

  // Spark burst from a given anchor element, relative to a positioned host.
  // Re-fires on every call (throttled by `gap` ms). Used on review-card hover.
  window.MT_sparkBurst = function (host, anchor, gap = 700) {
    if (!host || !anchor) return;
    if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const now = Date.now();
    if (host._lastSpark && now - host._lastSpark < gap) return;
    host._lastSpark = now;
    const hr = host.getBoundingClientRect();
    const ar = anchor.getBoundingClientRect();
    const cx = ar.left - hr.left + ar.width / 2;
    const cy = ar.top - hr.top + ar.height / 2;
    const N = 12;
    for (let i = 0; i < N; i++) {
      const sp = document.createElement("span");
      sp.className = "pdp2-spark";
      const ang = (Math.PI * 2 * i) / N + (Math.random() - 0.5) * 0.5;
      const dist = 26 + Math.random() * 34;
      sp.style.setProperty("--x", cx + "px");
      sp.style.setProperty("--y", cy + "px");
      sp.style.setProperty("--dx", Math.cos(ang) * dist + "px");
      sp.style.setProperty("--dy", Math.sin(ang) * dist + "px");
      sp.style.setProperty("--dur", (0.6 + Math.random() * 0.4) + "s");
      const sc = 0.5 + Math.random() * 0.8;
      sp.style.width = sp.style.height = (6 * sc) + "px";
      host.appendChild(sp);
      setTimeout(() => sp.remove(), 1100);
    }
  };

  // Spawn a short radial particle burst from a stat chip.
  window.MT_burstChip = function (chip) {
    if (!chip || chip.dataset.burst === "1") return;
    chip.dataset.burst = "1";
    if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const r = chip.getBoundingClientRect();
    const cx = r.width / 2, cy = r.height / 2;
    const N = 14;
    for (let i = 0; i < N; i++) {
      const sp = document.createElement("span");
      sp.className = "pdp2-spark";
      const ang = (Math.PI * 2 * i) / N + (Math.random() - 0.5) * 0.5;
      const dist = 34 + Math.random() * 40;
      sp.style.setProperty("--x", cx + "px");
      sp.style.setProperty("--y", cy + "px");
      sp.style.setProperty("--dx", Math.cos(ang) * dist + "px");
      sp.style.setProperty("--dy", Math.sin(ang) * dist + "px");
      sp.style.setProperty("--dur", (0.7 + Math.random() * 0.5) + "s");
      const sc = 0.5 + Math.random() * 0.9;
      sp.style.width = sp.style.height = (6 * sc) + "px";
      chip.appendChild(sp);
      setTimeout(() => sp.remove(), 1300);
    }
    chip.classList.add("is-bursting");
    setTimeout(() => chip.classList.remove("is-bursting"), 700);
  };

  // Immediately animate any [data-countup] inside `root` (no scroll needed).
  // Used when content is revealed by an accordion/tab so it can't rely on
  // the element having scrolled into view.
  window.MT_runCountUp = function (root = document) {
    root.querySelectorAll("[data-countup]").forEach((el, idx) => {
      if (el.dataset.cuDone === "1") return;
      el.dataset.cuDone = "1";
      el.dataset.cuOn = "1"; // stop the IO-based init from double-running
      const to = parseFloat(el.dataset.to || "0");
      const from = parseFloat(el.dataset.from || "0");
      const dur = parseFloat(el.dataset.duration || "2") * 1000;
      const chip = el.closest(".pdp2-statchip");
      const begin = () => {
        const start = performance.now();
        const tick = (now) => {
          const t = Math.min((now - start) / dur, 1);
          const eased = 1 - Math.pow(1 - t, 4);
          el.textContent = String(Math.round(from + (to - from) * eased));
          if (t < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      };
      setTimeout(begin, idx * 160);
    });
  };

  window.MT_initCountUp = function (root = document) {
    root.querySelectorAll("[data-countup]:not([data-cu-on])").forEach((el) => {
      el.dataset.cuOn = "1";
      const to = parseFloat(el.dataset.to || "0");
      const from = parseFloat(el.dataset.from || "0");
      const dur = parseFloat(el.dataset.duration || "2") * 1000;
      el.textContent = String(Math.round(from));
      const obs = new IntersectionObserver((entries) => {
        if (!entries[0].isIntersecting) return;
        obs.disconnect();
        const start = performance.now();
        const tick = (now) => {
          const t = Math.min((now - start) / dur, 1);
          const eased = 1 - Math.pow(1 - t, 4); // power3.out
          el.textContent = String(Math.round(from + (to - from) * eased));
          if (t < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      }, { threshold: 0.2 });
      obs.observe(el);
    });
  };

  // ──────────────────────────────────────────────────────────────
  // 4. Magic Bento: spotlight + tilt + magnetism + particle burst
  // ──────────────────────────────────────────────────────────────
  // Applied to .mt-bento grids; each direct .mt-bento-card child gets
  // tilt on mousemove, a global spotlight follows the cursor across
  // the grid, and clicking a card emits a radial ripple.
  const PROX = 60, FADE = 80, RADIUS = 220;
  let spotEl = null;
  const ensureSpot = () => {
    if (spotEl) return spotEl;
    spotEl = document.createElement("div");
    spotEl.className = "mt-bento-spot";
    document.body.appendChild(spotEl);
    return spotEl;
  };

  window.MT_initBento = function (root = document) {
    root.querySelectorAll(".mt-bento").forEach((grid) => {
      // Re-init safe: drop the previous document-level handler so a fresh
      // card NodeList is captured (filter/sort re-renders swap card nodes).
      if (grid.__btMove) document.removeEventListener("mousemove", grid.__btMove);
      grid.dataset.bentoOn = "1";
      const spot = ensureSpot();
      const cards = grid.querySelectorAll(".mt-bento-card");

      cards.forEach((card) => {
        if (card.__btOn) return; // per-card listeners attach once only
        card.__btOn = true;
        let hovering = false;
        const particles = [];

        const spawn = () => {
          if (!hovering) return;
          for (let i = 0; i < 6; i++) {
            const p = document.createElement("div");
            p.className = "mt-bento-particle";
            const [r, g, b] = ACCENT_RGB();
            p.style.background = `rgba(${r},${g},${b},1)`;
            p.style.boxShadow = `0 0 5px rgba(${r},${g},${b},.6)`;
            const x = Math.random() * card.clientWidth;
            const y = Math.random() * card.clientHeight;
            p.style.left = x + "px";
            p.style.top = y + "px";
            card.appendChild(p);
            particles.push(p);
            const dx = (Math.random() - 0.5) * 30;
            const dy = (Math.random() - 0.5) * 30;
            p.animate(
              [{ transform: "translate(0,0)", opacity: 1 },
               { transform: `translate(${dx}px,${dy}px)`, opacity: 0 }],
              { duration: 1800 + Math.random() * 800, easing: "ease-out", fill: "forwards" }
            );
            setTimeout(() => { p.remove(); }, 2600);
          }
        };
        const clearParticles = () => {
          particles.splice(0).forEach((p) => p.remove());
        };

        card.addEventListener("mouseenter", () => {
          hovering = true;
          spawn();
        });
        card.addEventListener("mousemove", (e) => {
          const r = card.getBoundingClientRect();
          const x = e.clientX - r.left, y = e.clientY - r.top;
          const cx = r.width / 2, cy = r.height / 2;
          // tilt + slight magnetism via CSS vars; CSS rule on .mt-bento-card
          // reads them with a smooth transition
          card.style.setProperty("--tx", ((x - cx) * 0.04).toFixed(2) + "px");
          card.style.setProperty("--ty", ((y - cy) * 0.04).toFixed(2) + "px");
          card.style.setProperty("--rx", (((y - cy) / cy) * -7).toFixed(2) + "deg");
          card.style.setProperty("--ry", (((x - cx) / cx) * 7).toFixed(2) + "deg");
        });
        card.addEventListener("mouseleave", () => {
          hovering = false;
          clearParticles();
          card.style.setProperty("--glow-intensity", "0");
          card.style.setProperty("--tx", "0px");
          card.style.setProperty("--ty", "0px");
          card.style.setProperty("--rx", "0deg");
          card.style.setProperty("--ry", "0deg");
        });
        card.addEventListener("click", (e) => {
          const r = card.getBoundingClientRect();
          const x = e.clientX - r.left, y = e.clientY - r.top;
          const maxD = Math.max(
            Math.hypot(x, y),
            Math.hypot(x - r.width, y),
            Math.hypot(x, y - r.height),
            Math.hypot(x - r.width, y - r.height)
          );
          const [rd, gd, bd] = ACCENT_RGB();
          const rpl = document.createElement("div");
          rpl.style.cssText = `position:absolute;width:${maxD * 2}px;height:${maxD * 2}px;border-radius:50%;background:radial-gradient(circle,rgba(${rd},${gd},${bd},.3) 0%,rgba(${rd},${gd},${bd},.12) 35%,transparent 70%);left:${x - maxD}px;top:${y - maxD}px;pointer-events:none;z-index:5;`;
          card.appendChild(rpl);
          rpl.animate(
            [{ transform: "scale(0)", opacity: 1 },
             { transform: "scale(1)", opacity: 0 }],
            { duration: 700, easing: "ease-out", fill: "forwards" }
          );
          setTimeout(() => rpl.remove(), 700);
        });
      });

      // Document-level mousemove → spotlight follows cursor inside grid
      const onMove = (e) => {
        const gr = grid.getBoundingClientRect();
        const inside = e.clientX >= gr.left && e.clientX <= gr.right
                    && e.clientY >= gr.top  && e.clientY <= gr.bottom;
        if (!inside) {
          spot.style.opacity = "0";
          cards.forEach((c) => c.style.setProperty("--glow-intensity", "0"));
          return;
        }
        spot.style.left = e.clientX + "px";
        spot.style.top = e.clientY + "px";
        const [r, g, b] = ACCENT_RGB();
        spot.style.background = `radial-gradient(circle, rgba(${r},${g},${b},0.13) 0%, rgba(${r},${g},${b},0.07) 20%, rgba(${r},${g},${b},0.03) 40%, transparent 65%)`;

        let minD = Infinity;
        cards.forEach((card) => {
          const cr = card.getBoundingClientRect();
          const cx = cr.left + cr.width / 2, cy = cr.top + cr.height / 2;
          const d = Math.max(0, Math.hypot(e.clientX - cx, e.clientY - cy) - Math.max(cr.width, cr.height) / 2);
          minD = Math.min(minD, d);
          const intensity = d <= PROX ? 1 : d <= FADE ? (FADE - d) / (FADE - PROX) : 0;
          card.style.setProperty("--glow-x", ((e.clientX - cr.left) / cr.width * 100) + "%");
          card.style.setProperty("--glow-y", ((e.clientY - cr.top) / cr.height * 100) + "%");
          card.style.setProperty("--glow-intensity", intensity.toFixed(3));
          card.style.setProperty("--glow-radius", RADIUS + "px");
          card.style.setProperty("--glow-color", `${r},${g},${b}`);
        });
        spot.style.opacity = String(minD <= PROX ? 0.75 : minD <= FADE ? ((FADE - minD) / (FADE - PROX)) * 0.75 : 0);
      };
      grid.__btMove = onMove;
      document.addEventListener("mousemove", onMove);
    });
  };

  // ──────────────────────────────────────────────────────────────
  // 5. Border-glow (cursor-tracking conic mask) for .mt-glow-card
  // ──────────────────────────────────────────────────────────────
  // Sets two CSS vars on pointermove that the CSS rules in styles.css
  // consume to mask the gradient halo + edge light to the cursor side.
  window.MT_initGlowCards = function (root = document) {
    const getCx = (el) => {
      const r = el.getBoundingClientRect();
      return [r.width / 2, r.height / 2];
    };
    const edgeProx = (el, x, y) => {
      const [cx, cy] = getCx(el);
      const dx = x - cx, dy = y - cy;
      const kx = dx ? cx / Math.abs(dx) : Infinity;
      const ky = dy ? cy / Math.abs(dy) : Infinity;
      return Math.min(Math.max(1 / Math.min(kx, ky), 0), 1);
    };
    const cursorAngle = (el, x, y) => {
      const [cx, cy] = getCx(el);
      const dx = x - cx, dy = y - cy;
      if (!dx && !dy) return 0;
      let d = Math.atan2(dy, dx) * (180 / Math.PI) + 90;
      return d < 0 ? d + 360 : d;
    };
    root.querySelectorAll(".mt-glow-card:not([data-gc-on])").forEach((el) => {
      el.dataset.gcOn = "1";
      el.addEventListener("pointermove", (e) => {
        const r = el.getBoundingClientRect();
        const x = e.clientX - r.left, y = e.clientY - r.top;
        el.style.setProperty("--edge-proximity", (edgeProx(el, x, y) * 100).toFixed(2));
        el.style.setProperty("--cursor-angle", cursorAngle(el, x, y).toFixed(2) + "deg");
      });
      el.addEventListener("pointerleave", () => el.style.setProperty("--edge-proximity", "0"));
    });
  };

  // ──────────────────────────────────────────────────────────────
  // 6. Sweep on card hover (.mt-sweep-card)
  // ──────────────────────────────────────────────────────────────
  // A horizontal accent-color light bar slides L→R on enter, R→L on leave.
  window.MT_initSweep = function (root = document) {
    root.querySelectorAll(".mt-sweep-card:not([data-sw-on])").forEach((card) => {
      card.dataset.swOn = "1";
      card.addEventListener("mouseenter", () => {
        card.classList.remove("is-sweep-rev");
        // force reflow so re-adding sweep class restarts animation
        void card.offsetWidth;
        card.classList.add("is-sweep-fwd");
      });
      card.addEventListener("mouseleave", () => {
        card.classList.remove("is-sweep-fwd");
        void card.offsetWidth;
        card.classList.add("is-sweep-rev");
        setTimeout(() => card.classList.remove("is-sweep-rev"), 1000);
      });
    });
  };

  // ──────────────────────────────────────────────────────────────
  // Global init: call after React mounts. Components.jsx pings this
  // on a microtask + once again after a short delay (so pages that
  // lazy-mount sections still get covered).
  // ──────────────────────────────────────────────────────────────
  window.MT_initAnimations = function () {
    window.MT_initTypewriter();
    window.MT_initWordReveal();
    window.MT_initReveal();
    window.MT_initCountUp();
    window.MT_initBento();
    window.MT_initGlowCards();
    window.MT_initSweep();
  };
})();

/* ── Part 2: page behaviour (vanilla port of the React page) ─────────── */
(function () {
  var RM = function () { return matchMedia('(prefers-reduced-motion: reduce)').matches; };

  function once(el, fn, opts) {
    if (!('IntersectionObserver' in window)) { fn(); return; }
    var io = new IntersectionObserver(function (es) {
      if (es.some(function (e) { return e.isIntersecting; })) { io.disconnect(); fn(); }
    }, opts || { threshold: .3 });
    io.observe(el);
  }

  /* Hero typewriter: "|" = line break, "{…}" = accent run. Starts in view. */
  function typeH1(h) {
    if (h.dataset.meOn) return; h.dataset.meOn = '1';
    var text = h.dataset.text || '';
    var printable = text.replace(/[{}]/g, '').length;
    var caret = '<i class="dv-caret" aria-hidden="true"></i>';
    function render(n) {
      var out = '', inEm = false, shown = 0, buf = '';
      function flush() { if (!buf) return; out += inEm ? '<em>' + buf + '</em>' : buf; buf = ''; }
      for (var i = 0; i < text.length; i++) {
        var ch = text[i];
        if (ch === '{') { flush(); inEm = true; continue; }
        if (ch === '}') { flush(); inEm = false; continue; }
        if (shown >= n) break;
        shown++;
        if (ch === '|') { flush(); out += '<br>'; continue; }
        buf += ch.replace(/&/g, '&amp;').replace(/</g, '&lt;');
      }
      flush();
      h.innerHTML = out + caret;
      h.classList.toggle('is-done', n >= printable);
    }
    if (RM()) { render(printable); return; }
    render(0);
    once(h, function () {
      var n = 0;
      setTimeout(function step() { n++; render(n); if (n < printable) setTimeout(step, 40); }, 260);
    }, { threshold: .2 });
  }

  /* Metric count-ups: a leading number with any suffix ("100%", "12 mo"). */
  function countUp(el) {
    if (el.dataset.meOn) return; el.dataset.meOn = '1';
    var m = String(el.dataset.value || el.textContent).match(/^([\d.]+)(.*)$/);
    if (!m || RM()) return;
    var target = parseFloat(m[1]), suffix = m[2];
    el.textContent = '0' + suffix;
    once(el, function () {
      var t0 = performance.now();
      (function tick(t) {
        /* a frame's timestamp can predate t0; clamp so it never counts below 0 */
        var p = Math.min(1, Math.max(0, (t - t0) / 1400));
        el.textContent = Math.round(target * (1 - Math.pow(1 - p, 3))) + suffix;
        if (p < 1) requestAnimationFrame(tick);
      })(t0);
    }, { threshold: .4 });
  }

  /* Device category index: hover, focus or click swaps the preview. */
  function browse(b) {
    if (b.dataset.meOn) return; b.dataset.meOn = '1';
    var tabs = [].slice.call(b.querySelectorAll('.av4-index-b'));
    var pv = b.querySelector('.av4-preview'), ph = pv.querySelector('.av4-preview-ph');
    var h3 = pv.querySelector('h3'), p = pv.querySelector('.av4-preview-body p');
    var cur = 0;
    function show(i) {
      var t = tabs[i]; if (!t || i === cur) return; cur = i;
      tabs.forEach(function (x, j) { var on = j === i; x.classList.toggle('is-on', on); x.setAttribute('aria-selected', on); });
      pv.setAttribute('aria-labelledby', t.id);
      var title = t.querySelector('.av4-index-t').textContent, d = t.dataset;
      /* a fresh node replays the fade-in, like the React key swap */
      var el = document.createElement('div');
      if (d.img) {
        el.className = 'me-dev-ph' + (d.fit === 'cover' ? ' is-cover' : '');
        el.setAttribute('role', 'img'); el.setAttribute('aria-label', title);
        el.style.setProperty('--me-img', 'url("' + d.img + '")');
      } else {
        el.className = 'me-dev-ph is-empty';
        el.innerHTML = t.querySelector('i').innerHTML + '<span></span>';
        el.querySelector('span').textContent = title + ' photo coming soon';
        var svg = el.querySelector('svg'); if (svg) { svg.setAttribute('width', 44); svg.setAttribute('height', 44); }
      }
      ph.innerHTML = ''; ph.appendChild(el);
      h3.textContent = title; p.textContent = d.text || '';
    }
    tabs.forEach(function (t, i) {
      t.addEventListener('mouseenter', function () { show(i); });
      t.addEventListener('focus', function () { show(i); });
      t.addEventListener('click', function () { show(i); });
    });
  }

  /* Phones · category accordion: one row open at a time; an opened row
     scrolls into view if it starts under the header or low on the screen. */
  function accordion(acc) {
    if (acc.dataset.meOn) return; acc.dataset.meOn = '1';
    var rows = [].slice.call(acc.querySelectorAll('.mec-row'));
    rows.forEach(function (li) {
      li.querySelector('.mec-head').addEventListener('click', function () {
        var open = !li.classList.contains('is-open');
        rows.forEach(function (r) {
          var on = open && r === li;
          r.classList.toggle('is-open', on);
          r.querySelector('.mec-head').setAttribute('aria-expanded', on);
        });
        if (!open) return;
        var ph = li.querySelector('.mec-ph[data-img]');
        if (ph) { ph.style.setProperty('--me-img', 'url("' + ph.dataset.img + '")'); ph.removeAttribute('data-img'); }
        setTimeout(function () {
          var hd = document.querySelector('.shopify-section-header-sticky, sticky-header');
          var off = (hd ? Math.max(0, hd.getBoundingClientRect().bottom) : 64) + 12;
          var top = li.getBoundingClientRect().top;
          if (top < off || top > window.innerHeight * .55) window.scrollTo({ top: top + window.scrollY - off, behavior: RM() ? 'auto' : 'smooth' });
        }, 320);
      });
    });
  }

  /* Phones · featured swipe: dots, arrows and the "01 / 08" counter follow
     the scroll. Mouse users can drag the cards as well as use the arrows. */
  function featSwipe(el) {
    if (el.dataset.meOn) return; el.dataset.meOn = '1';
    var track = el.querySelector('.mfe-track'), dots = [].slice.call(el.querySelectorAll('.mfe-dot'));
    var arrows = [].slice.call(el.querySelectorAll('.mfe-arr'));
    var count = el.querySelector('.mfe-count'), n = dots.length, on = 0;
    var pad = function (x) { return (x < 10 ? '0' : '') + x; };
    function step() { var c = track.querySelector('.mfe-card'); return c ? c.offsetWidth + 12 : 1; }
    function go(i) { track.scrollTo({ left: Math.max(0, Math.min(n - 1, i)) * step(), behavior: RM() ? 'auto' : 'smooth' }); }
    function sync() {
      var end = track.scrollLeft >= track.scrollWidth - track.clientWidth - 2;
      on = end ? n - 1 : Math.max(0, Math.min(n - 1, Math.round(track.scrollLeft / step())));
      dots.forEach(function (d, i) { d.classList.toggle('is-on', i === on); if (i === on) d.setAttribute('aria-current', 'true'); else d.removeAttribute('aria-current'); });
      count.textContent = pad(on + 1) + ' / ' + pad(n);
      arrows.forEach(function (a) { a.disabled = a.dataset.dir < 0 ? on === 0 : on === n - 1; });
    }
    track.addEventListener('scroll', sync, { passive: true });
    dots.forEach(function (d, i) { d.addEventListener('click', function () { go(i); }); });
    arrows.forEach(function (a) { a.addEventListener('click', function () { go(on + +a.dataset.dir); }); });
    var drag = null;
    track.addEventListener('pointerdown', function (e) {
      if (e.pointerType !== 'mouse' || e.button !== 0) return;
      drag = { x: e.clientX, left: track.scrollLeft, moved: false };
    });
    window.addEventListener('pointermove', function (e) {
      if (!drag) return;
      var dx = e.clientX - drag.x;
      if (!drag.moved && Math.abs(dx) > 5) { drag.moved = true; track.classList.add('is-drag'); }
      if (drag.moved) track.scrollLeft = drag.left - dx;
    });
    window.addEventListener('pointerup', function () {
      if (!drag) return;
      var moved = drag.moved; drag = null;
      if (!moved) return;
      track.classList.remove('is-drag');
      go(Math.round(track.scrollLeft / step()));
      /* a drag that ends on a card must not open its link */
      var k = function (ev) { ev.preventDefault(); ev.stopPropagation(); };
      track.addEventListener('click', k, true);
      setTimeout(function () { track.removeEventListener('click', k, true); }, 0);
    });
    track.addEventListener('dragstart', function (e) { e.preventDefault(); });
  }

  /* Phones · New / Recertified tabs: show one column; the card fades in again. */
  function cmpTabs(el) {
    if (el.dataset.meOn) return; el.dataset.meOn = '1';
    var tabs = [].slice.call(el.querySelectorAll('.dv-cmp2-tab')), card = el.querySelector('.dv-cmp2-card');
    tabs.forEach(function (t, i) {
      t.addEventListener('click', function () {
        tabs.forEach(function (x, j) { x.classList.toggle('is-on', j === i); x.setAttribute('aria-selected', j === i); });
        card.querySelectorAll('b[data-v0]').forEach(function (b) { b.textContent = b.getAttribute('data-v' + i); });
        card.style.animation = 'none'; void card.offsetWidth; card.style.animation = '';
      });
    });
  }

  function init(root) {
    root.querySelectorAll('.me-lp h1.dv-type[data-text]').forEach(typeH1);
    root.querySelectorAll('.me-lp .dv-metric b').forEach(countUp);
    root.querySelectorAll('.me-lp .av4-browse').forEach(browse);
    root.querySelectorAll('.me-lp .mec-acc').forEach(accordion);
    root.querySelectorAll('.me-lp .mfe').forEach(featSwipe);
    root.querySelectorAll('.me-lp .dv-cmp2').forEach(cmpTabs);
    root.querySelectorAll('.me-lp .dv-in:not(.is-in)').forEach(function (el) { once(el, function () { el.classList.add('is-in'); }, { threshold: .12 }); });
    root.querySelectorAll('.me-lp .dv-to-top').forEach(function (b) {
      if (b.dataset.meOn) return; b.dataset.meOn = '1';
      function h() { b.classList.toggle('is-on', window.scrollY > 600); }
      h(); window.addEventListener('scroll', h, { passive: true });
      b.addEventListener('click', function () { window.scrollTo({ top: 0, behavior: RM() ? 'auto' : 'smooth' }); });
    });
    window.MT_initReveal && window.MT_initReveal(root);
    window.MT_initBento && window.MT_initBento(root);
  }

  function boot() { if (document.querySelector('.me-lp')) init(document); }
  if (document.readyState !== 'loading') boot(); else document.addEventListener('DOMContentLoaded', boot);
  document.addEventListener('shopify:section:load', function (e) { if (e.target.querySelector('.me-lp')) init(e.target); });
})();
}
